import type { PokiAdapter } from '@/sdk/PokiAdapter';
import { detectAdblock } from '@/sdk/adblockDetect';

export const DEFAULT_MIN_COMMERCIAL_GAP_MS = 180_000;
export const DEFAULT_POST_LOAD_COOLDOWN_MS = 30_000;

export type CommercialBlockedReason =
  | 'loading-not-finished'
  | 'first-game'
  | 'post-load-cooldown'
  | 'min-gap'
  | 'back-to-back'
  | 'gameplay-active'
  | 'ads-disabled';

export type RewardedBlockedReason =
  | 'loading-not-finished'
  | 'gameplay-active'
  | 'ads-disabled'
  | 'adblocker';

export type CommercialResult = { shown: true } | { shown: false; reason: CommercialBlockedReason };

export type RewardedResult =
  | { shown: true; rewarded: boolean }
  | { shown: false; reason: RewardedBlockedReason };

export interface AdGateOptions {
  adapter: PokiAdapter;
  now?: () => number;
  minCommercialGapMs?: number;
  postLoadCooldownMs?: number;
  adsEnabled?: boolean;
  // Override only in tests; production passes `detectAdblock` from adblockDetect.
  detectAdblock?: () => Promise<boolean>;
}

export class AdGate {
  private readonly adapter: PokiAdapter;
  private readonly now: () => number;
  private readonly minCommercialGapMs: number;
  private readonly postLoadCooldownMs: number;
  private readonly adsEnabled: boolean;
  private readonly detectAdblock: () => Promise<boolean>;

  private loadingFinishedAt: number | null = null;
  private lastAdAt: number | null = null;
  private gameplayStartSinceLastAd = false;
  private gameplayActive = false;
  private gamesCompleted = 0;

  constructor(options: AdGateOptions) {
    this.adapter = options.adapter;
    this.now = options.now ?? (() => Date.now());
    this.minCommercialGapMs = options.minCommercialGapMs ?? DEFAULT_MIN_COMMERCIAL_GAP_MS;
    this.postLoadCooldownMs = options.postLoadCooldownMs ?? DEFAULT_POST_LOAD_COOLDOWN_MS;
    this.adsEnabled = options.adsEnabled ?? true;
    this.detectAdblock = options.detectAdblock ?? detectAdblock;
  }

  notifyGameLoadingFinished(): void {
    this.loadingFinishedAt = this.now();
  }

  notifyGameplayStart(): void {
    this.gameplayActive = true;
    this.gameplayStartSinceLastAd = true;
    this.adapter.gameplayStart();
  }

  notifyGameplayStop(): void {
    if (!this.gameplayActive) return;
    this.gameplayActive = false;
    this.gamesCompleted += 1;
    this.adapter.gameplayStop();
  }

  async maybeShowCommercial(): Promise<CommercialResult> {
    const blocked = this.checkCommercialRules();
    if (blocked) return { shown: false, reason: blocked };

    await this.adapter.commercialBreak();
    this.lastAdAt = this.now();
    this.gameplayStartSinceLastAd = false;
    return { shown: true };
  }

  async showRewarded(): Promise<RewardedResult> {
    const blocked = this.checkRewardedRules();
    if (blocked) return { shown: false, reason: blocked };

    if (await this.detectAdblock()) return { shown: false, reason: 'adblocker' };

    const rewarded = await this.adapter.rewardedBreak();
    this.lastAdAt = this.now();
    this.gameplayStartSinceLastAd = false;
    return { shown: true, rewarded };
  }

  private checkCommercialRules(): CommercialBlockedReason | null {
    if (!this.adsEnabled) return 'ads-disabled';
    if (this.gameplayActive) {
      console.error('[AdGate] commercialBreak requested during active gameplay — blocked');
      return 'gameplay-active';
    }
    if (this.loadingFinishedAt === null) return 'loading-not-finished';
    if (this.gamesCompleted < 2) return 'first-game';
    const now = this.now();
    if (now - this.loadingFinishedAt < this.postLoadCooldownMs) return 'post-load-cooldown';
    if (this.lastAdAt !== null) {
      if (now - this.lastAdAt < this.minCommercialGapMs) return 'min-gap';
      if (!this.gameplayStartSinceLastAd) return 'back-to-back';
    }
    return null;
  }

  private checkRewardedRules(): RewardedBlockedReason | null {
    if (!this.adsEnabled) return 'ads-disabled';
    if (this.gameplayActive) {
      console.error('[AdGate] rewardedBreak requested during active gameplay — blocked');
      return 'gameplay-active';
    }
    if (this.loadingFinishedAt === null) return 'loading-not-finished';
    return null;
  }
}
